import { useMemo, useState } from "react";
import { Pressable, StyleSheet, Text, TextInput, View } from "react-native";
import { useTranslation } from "react-i18next";
import { buildDayPanel } from "@heraion/shared";
import { resolveTheme } from "../theme";
import { useThemePreferenceContext } from "../ThemeContext";
import { UnsavedChangesDialog } from "../primitives/UnsavedChangesDialog";
import { Toast } from "../primitives/Toast";
import { EditorSaveStatus } from "../ProgramEditor/EditorSaveStatus";
import { DraftResumeDialog } from "../ProgramEditor/DraftResumeDialog";
import type { StoredEditorDraft } from "../ProgramEditor/useDraftResume";

import { makeEditorStyles } from "../ProgramEditor/styles";
import { useBreakpoint } from "../useBreakpoint";
import { makeComposerStyles } from "./composerStyles";
import { makePlanStyles } from "./planComposerStyles";
import { useComposerGuard } from "./useComposerGuard";
import { useEditInPlace } from "./useEditInPlace";
import { useKindLabels } from "./useKindLabels";
import { ComposerAssigneesBand } from "./ComposerAssigneesBand";
import { ComposerEditLayout } from "./ComposerEditLayout";
import { PlanExerciseEditor } from "./PlanExerciseEditor";
import type { ProgramComposerProps } from "./types";

// Single-mode (Plan) editor — the "edit-where-the-exercise-sits" rework
// (next/plans-editor-usability.md). It replaces the program composer's
// compose-form-on-top (which forced select-at-bottom → edit-at-top scroll
// churn) with the shared edit-in-place shell (ComposerEditLayout +
// useEditInPlace): narrow = accordion, wide = master-detail. cycleWeeks is
// pinned to 1 → every entry is one block on day 0, week 0;
// buildDayPanel(blocks, 0) is the exercise list. This file is now just the
// Plan-specific meta band + the per-exercise editor (PlanExerciseEditor) +
// the picker; the layout, the list rows, and the live staging→commit state
// machine are the same pieces the program composer uses.
export function PlanComposer({
  apiFetch,
  durableWrite,
  dark,
  plan,
  catalog,
  Picker,
  DateField,
  onSaved,
  onCancel,
  registerBackGuard,
  unitsPreference,
  callerSub,
  athleteRoster,
  authoredAs,
  keepOpen,
  draftScope,
  draftOwner,
  stored = null,
}: ProgramComposerProps & { stored?: StoredEditorDraft | null }) {
  const { t } = useTranslation();
  const { wide } = useBreakpoint();
  const family = useThemePreferenceContext()?.preference.family ?? "brand";
  const theme = resolveTheme(dark, family);
  const p = theme.palette;
  const cs = useMemo(() => makeComposerStyles(p), [p]);
  const ps = useMemo(() => makePlanStyles(p), [p]);
  const selectStyles = useMemo(() => makeEditorStyles(p), [p]);

  // #76: scheduledFor is part of the compared value (see useComposerGuard).
  // #336: plus the device draft, the resume question and the save line.
  const { composer, guard, saveState, toast, conflict } = useComposerGuard(
    {
      apiFetch,
      keepOpen,
      durableWrite,
      catalog,
      onSaved,
      mode: "single",
      plan,
      unitsPreference,
      authoredAs,
    },
    {
      onLeave: onCancel,
      registerBackGuard,
      // A staged exercise form (open edit with changes, or a new exercise
      // being composed) is not in the draft yet; Back would drop it.
      extraDirty: (c) =>
        c.formDirty || (c.form.editing === null && c.form.members.length > 0),
      draftScope,
      stored,
      callerSub: draftOwner,
    },
  );
  const { draft, setDraft, busy, error, blocks, form, formConflict, cellUnit } =
    composer;
  const kindLabels = useKindLabels(cellUnit);
  const { scheduledFor, setScheduledFor } = composer;

  const entries = useMemo(
    () => buildDayPanel(blocks, 0, kindLabels),
    [blocks, kindLabels],
  );
  const resolveName = useMemo(() => {
    const bySlug = new Map(catalog.map((c) => [c.slug, c.name]));
    return (id: string, snapshot?: string) => snapshot ?? bySlug.get(id) ?? id;
  }, [catalog]);

  const ip = useEditInPlace({ composer, entries });
  const [detailsOpen, setDetailsOpen] = useState(
    () => scheduledFor !== "" || draft.description !== "",
  );

  // Exclude EVERY exercise already in the plan (committed entries + the open
  // editor's members) from the picker. A single-workout plan never wants the
  // same exercise twice, and — since the picker is the only add/swap path —
  // this makes a real slug conflict at commit() unreachable.
  const presentSlugs = useMemo(() => {
    const s = new Set(form.members.map((m) => m.exerciseId));
    for (const e of entries) {
      if (e.kind === "composed") for (const m of e.members) s.add(m.exerciseId);
      else s.add(e.exerciseId);
    }
    return [...s];
  }, [form.members, entries]);

  const metaBand = (
    <View style={ps.metaBand}>
      <Text style={cs.fieldLabel}>{t("plans.name", { defaultValue: "Name" })}</Text>
      <TextInput
        style={cs.input}
        value={draft.name}
        onChangeText={(v) => setDraft((d) => ({ ...d, name: v }))}
        placeholder={t("plans.namePlaceholder", { defaultValue: "e.g. Squat day" })}
        placeholderTextColor={p.ink3}
        accessibilityLabel={t("plans.name", { defaultValue: "Name" })}
      />
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded: detailsOpen }}
        onPress={() => setDetailsOpen((o) => !o)}
        style={ps.disclosureBtn}
      >
        <Text style={ps.disclosureText}>
          {detailsOpen ? "▾" : "▸"} {t("plans.details", { defaultValue: "Details" })}
        </Text>
      </Pressable>
      {detailsOpen ? (
        <>
          <Text style={cs.fieldLabel}>
            {t("plans.scheduledFor", { defaultValue: "Scheduled for (optional)" })}
          </Text>
          {DateField ? (
            <DateField value={scheduledFor} onChange={setScheduledFor} />
          ) : (
            <TextInput
              style={cs.input}
              value={scheduledFor}
              onChangeText={setScheduledFor}
              placeholder="YYYY-MM-DD"
              placeholderTextColor={p.ink3}
              autoCapitalize="none"
              accessibilityLabel={t("plans.scheduledFor", {
                defaultValue: "Scheduled for (optional)",
              })}
            />
          )}
          <Text style={cs.fieldLabel}>
            {t("plans.notes", { defaultValue: "Notes (optional)" })}
          </Text>
          <TextInput
            style={[cs.input, cs.inputMultiline]}
            value={draft.description}
            onChangeText={(v) => setDraft((d) => ({ ...d, description: v }))}
            multiline
            placeholderTextColor={p.ink3}
            accessibilityLabel={t("plans.notes", { defaultValue: "Notes (optional)" })}
          />
        </>
      ) : null}
      {/* PE-48 — same band the program path mounts (ComposerAssigneesBand),
          absent host seam → absent band, same degrade as every optional
          seam here. Always visible (not folded under Details): who a plan
          is shared with is not a minor detail.
          CO-20 — the gate is the VIEW, same reasoning as ProgramComposer's
          own assigneesBand: `authoredAs === "coach"` ⇒ Assegna. ⛔ The athlete
          side (Condividi) is built but NOT mounted — it is PE-51, an open
          decision of Antenore's. `athleteRoster` is data, not a gate. */}
      {callerSub && authoredAs === "coach" ? (
        <ComposerAssigneesBand
          theme={theme}
          apiFetch={apiFetch}
          kind="plans"
          authoredAs={authoredAs}
          callerSub={callerSub}
          athleteRoster={athleteRoster ?? []}
          assignedTo={draft.assignedTo}
          assigneeEmails={draft.assigneeEmails}
          onChangeAssignedTo={(next) => setDraft((d) => ({ ...d, assignedTo: next }))}
          onChangeAssigneeEmails={(next) =>
            setDraft((d) => ({ ...d, assigneeEmails: next }))
          }
        />
      ) : null}
    </View>
  );

  const editor = (
    <>
      {formConflict ? (
        <Text style={cs.errorText}>
          {t("composer.dupExercise", {
            name: resolveName(formConflict.slug),
            day: formConflict.dayIdx + 1,
            defaultValue: "{{name}} is already on D{{day}} in the selected weeks",
          })}
        </Text>
      ) : null}
      <PlanExerciseEditor
        composer={composer}
        resolveName={resolveName}
        cs={cs}
        ps={ps}
        selectStyles={selectStyles}
        theme={theme}
        onCollapse={ip.collapse}
        onRemove={ip.removeCurrent}
        onReplaceMember={(idx) => ip.setPicker({ replace: idx })}
      />
    </>
  );

  const picker = (
    <Picker
      visible={ip.picker !== null}
      onClose={() => ip.setPicker(null)}
      onAddMany={ip.onAddMany}
      onCreateSuperset={({ addSlugs }) => ip.onCreateSuperset(addSlugs)}
      presentSlugs={presentSlugs}
      // LG-41 — the ONE surface still hiding, and not by preference: a
      // PlannedExerciseEntry carries no id (plan.ts), so a second entry with
      // the same slug is ambiguous to everything resolving by slug (LG-40).
      presentTreatment="hide"
    />
  );

  // #336: the root View is the toast's positioned parent.
  return (
    <View style={rootStyle.root}>
      <ComposerEditLayout
        cs={cs}
        ps={ps}
        theme={theme}
        wide={wide}
        busy={busy}
        backLabel={t("plans.title", { defaultValue: "Plans" })}
        title={
          draft.name ||
          (plan
            ? t("plans.editingTitle", { defaultValue: "Edit plan" })
            : t("plans.creatingTitle", { defaultValue: "New plan" }))
        }
        onCancel={guard.handleBack}
        onSave={ip.handleSave}
        error={error}
        status={<EditorSaveStatus theme={theme} state={saveState} />}
        metaBand={metaBand}
        sectionLabel={t("plans.exercisesLabel", { defaultValue: "Exercises" })}
        emptyHint={t("plans.editorEmpty", {
          defaultValue: "No exercises yet. Add one to start building this plan.",
        })}
        selectPrompt={t("plans.selectExercisePrompt", {
          defaultValue: "Select an exercise to edit its sets.",
        })}
        entries={entries}
        resolveName={resolveName}
        editingIdx={ip.editingIdx}
        editing={ip.editing}
        composingNew={ip.composingNew}
        hasForm={ip.hasForm}
        editor={editor}
        onOpenEntry={ip.openEntry}
        onOpenAdd={ip.addExercise}
        addLabel={t("plans.addExerciseCta", { defaultValue: "Add exercise" })}
        picker={picker}
      />
      <UnsavedChangesDialog theme={theme} {...guard.dialogProps} />
      <DraftResumeDialog
        theme={theme}
        kind="conflict"
        visible={conflict.open}
        onKeep={conflict.onKeepDraft}
        onDrop={conflict.onTakeServer}
      />
      <Toast theme={theme} toast={toast.toast} onDismiss={toast.dismiss} />
      {toast.region}
    </View>
  );
}

// #336: the toast's positioned parent, the whole editor.
const rootStyle = StyleSheet.create({ root: { flex: 1 } });
